import { useCallback, useEffect, useMemo, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Select,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core"
import { useDebouncedValue } from "@mantine/hooks"
import { PageHeader, PageLayout } from "../../../components/crud"
import type { Column } from "../../../components/DataTable"
import { DataTable, useDataTable } from "../../../components/DataTable"
import { useUser } from "../../user/hooks/useUser"
import { downloadExport } from "../../../helpers/downloadExport"
import { useAgencies } from "../hooks/useAgencies"
import type { AdminUser } from "../models/adminUser"
import { AdminUserSchema, STATUS_MAP, TYPE_MAP } from "../models/adminUser"
import type { DrawerMode } from "./AdminUserDrawer"
import AdminUserDrawer from "./AdminUserDrawer"

export default function AdminUserList() {
  const { t } = useTranslation()
  const { data: userData } = useUser()
  const canManage = userData?.can?.manageUsers ?? false
  const canCreate = userData?.can?.manageUsers ?? false

  const { data: agencies } = useAgencies()

  const [username, setUsername] = useState("")
  const [firstName, setFirstName] = useState("")
  const [email, setEmail] = useState("")
  const [debouncedUsername] = useDebouncedValue(username, 400)
  const [debouncedFirstName] = useDebouncedValue(firstName, 400)
  const [debouncedEmail] = useDebouncedValue(email, 400)
  const [statusFilter, setStatusFilter] = useState<string | null>(null)
  const [typeFilter, setTypeFilter] = useState<string | null>(null)
  const [agencyFilter, setAgencyFilter] = useState<string | null>(null)

  const { data, isLoading, page, pageSize, sort, sortDir, handleSortChange, handlePageChange, updateFilters } =
    useDataTable<AdminUser>({
      queryKey: "admin-users",
      url: "admin-users",
      schema: AdminUserSchema,
      defaultSort: { field: "id", dir: "desc" },
      defaultPageSize: 50,
    })

  const exportFilters = useMemo(() => ({
    username: username || undefined,
    first_name: firstName || undefined,
    email: email || undefined,
    status: statusFilter && statusFilter !== "" ? statusFilter : undefined,
    type: typeFilter && typeFilter !== "" ? typeFilter : undefined,
    agency_id: agencyFilter && agencyFilter !== "" ? agencyFilter : undefined,
  }), [username, firstName, email, statusFilter, typeFilter, agencyFilter])

  useEffect(() => {
    const filters: Record<string, unknown> = {}
    if (debouncedUsername) filters.username = debouncedUsername
    if (debouncedFirstName) filters.first_name = debouncedFirstName
    if (debouncedEmail) filters.email = debouncedEmail
    if (statusFilter && statusFilter !== "") filters.status = statusFilter
    if (typeFilter && typeFilter !== "") filters.type = typeFilter
    if (agencyFilter && agencyFilter !== "") filters.agency_id = agencyFilter
    updateFilters(filters)
  }, [debouncedUsername, debouncedFirstName, debouncedEmail, statusFilter, typeFilter, agencyFilter, updateFilters])

  const [drawerOpened, setDrawerOpened] = useState(false)
  const [drawerMode, setDrawerMode] = useState<DrawerMode>("view")
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)
  const params = useParams<{ "*"?: string }>()
  const routeId = params["*"] ? Number(params["*"].split("/")[0]) : NaN

  const openDrawer = useCallback((id: number | null, mode: DrawerMode) => {
    setSelectedUserId(id)
    setDrawerMode(mode)
    setDrawerOpened(true)
  }, [])

  const closeDrawer = useCallback(() => {
    setDrawerOpened(false)
    setSelectedUserId(null)
  }, [])

  useEffect(() => {
    if (!Number.isNaN(routeId) && routeId > 0) {
      openDrawer(routeId, "view")
    }
  }, [routeId, openDrawer])

  const handleDeleted = useCallback(() => { closeDrawer() }, [closeDrawer])
  const handleSwitchToEdit = useCallback(() => { setDrawerMode("edit") }, [])

  const handleRowClick = useCallback(
    (v: AdminUser) => {
      if (selectedUserId === v.id && drawerOpened) {
        closeDrawer()
      } else {
        openDrawer(v.id, "view")
      }
    },
    [openDrawer, closeDrawer, selectedUserId, drawerOpened],
  )

  const columns: Column<AdminUser>[] = [
    {
      key: "username",
      label: t("adminUsers.username"),
      sortable: true,
      render: (v) => <Text fw={500} size="sm">{v.username}</Text>,
    },
    {
      key: "full_name",
      label: t("adminUsers.fullName"),
      sortable: true,
      render: (v) => <Text size="sm">{v.full_name}</Text>,
    },
    {
      key: "email",
      label: t("adminUsers.email"),
      sortable: true,
      render: (v) => <Text size="sm">{v.email}</Text>,
    },
    {
      key: "agency_name",
      label: t("adminUsers.agency"),
      sortable: false,
      render: (v) => <Text size="sm">{v.agency_name ?? "—"}</Text>,
    },
    {
      key: "type",
      label: t("adminUsers.type"),
      width: 60,
      render: (v) => {
        const info = TYPE_MAP[v.type]
        return info ? <Badge color={info.color} size="sm" variant="light">{info.label}</Badge> : null
      },
    },
    {
      key: "status",
      label: t("adminUsers.status"),
      sortable: true,
      width: 100,
      render: (v) => {
        const info = STATUS_MAP[v.status]
        return <Badge color={info?.color ?? "gray"} variant="light" size="sm">{t(`adminUsers.${info?.label ?? "inactive"}`)}</Badge>
      },
    },
  ]

  return (
    <PageLayout
      header={
        <PageHeader
          title={t("adminUsers.title")}
          actions={
            <Group>
              {canManage && (
                <Button
                  variant="default"
                  onClick={() => downloadExport("admin-users/export", "Utilizadores.xlsx", exportFilters as Record<string, string | undefined>)}
                  leftSection={<i className="fa fa-file-excel-o" />}
                >
                  <Trans i18nKey="common.export" />
                </Button>
              )}
              {canCreate && (
                <Button onClick={() => openDrawer(null, "create")} leftSection={<i className="fa fa-plus" />}>
                  <Trans i18nKey="adminUsers.create" />
                </Button>
              )}
            </Group>
          }
        />
      }
    >
      <Group mb="md" gap="sm" wrap="wrap">
        <TextInput
          placeholder={t("adminUsers.username")}
          value={username}
          onChange={(e) => setUsername(e.currentTarget.value)}
          style={{ width: 160 }}
        />
        <TextInput
          placeholder={t("adminUsers.firstName")}
          value={firstName}
          onChange={(e) => setFirstName(e.currentTarget.value)}
          style={{ width: 180 }}
        />
        <TextInput
          placeholder={t("adminUsers.email")}
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
          style={{ width: 200 }}
        />
        <Select
          placeholder={t("adminUsers.selectAgency")}
          value={agencyFilter}
          onChange={setAgencyFilter}
          data={(agencies ?? []).map((a: { id: number; name: string }) => ({ value: String(a.id), label: a.name }))}
          clearable
          searchable
          style={{ width: 200 }}
        />
        <Select
          placeholder={t("adminUsers.allTypes")}
          value={typeFilter}
          onChange={setTypeFilter}
          data={[
            { value: "1", label: "GPA" },
            { value: "2", label: "BEST" },
          ]}
          clearable
          style={{ width: 120 }}
        />
        <Select
          placeholder={t("adminUsers.allStatuses")}
          value={statusFilter}
          onChange={setStatusFilter}
          data={[
            { value: "10", label: t("adminUsers.active") },
            { value: "2", label: t("adminUsers.suspended") },
            { value: "0", label: t("adminUsers.inactive") },
          ]}
          clearable
          style={{ width: 140 }}
        />
      </Group>
      <DataTable<AdminUser>
        columns={columns}
        data={data?.items ?? []}
        total={data?.total ?? 0}
        page={page}
        pageSize={pageSize}
        sort={sort}
        sortDir={sortDir}
        loading={isLoading}
        emptyMessage={t("adminUsers.empty")}
        keyExtractor={(v) => v.id}
        onSortChange={handleSortChange}
        onPageChange={handlePageChange}
        onRowClick={handleRowClick}
        actions={(v) =>
          canManage ? (
            <Group gap={4} justify="flex-end" wrap="nowrap">
              <Tooltip label={t("common.edit")}>
                <ActionIcon variant="subtle" size="md" onClick={() => openDrawer(v.id, "edit")}>
                  <i className="fa fa-pencil" />
                </ActionIcon>
              </Tooltip>
            </Group>
          ) : undefined
        }
      />
      <AdminUserDrawer
        opened={drawerOpened}
        onClose={closeDrawer}
        mode={drawerMode}
        selectedUserId={selectedUserId}
        onDeleted={handleDeleted}
        onSwitchToEdit={handleSwitchToEdit}
      />
    </PageLayout>
  )
}
